import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  LinkOff as LinkOffIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import PageHeader from '../components/PageHeader';

interface ProviderUser {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName: string;
  roles: string[];
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
  updatedAt?: string;
  provider?: {
    id: number;
    name: string;
    type: string;
    status: string;
  };
}

interface ProviderUserFilters {
  search?: string;
  providerId?: number;
  role?: string;
  page?: number;
  limit?: number;
}

const ProviderUsersPage: React.FC = () => {
  const [filters, setFilters] = useState<ProviderUserFilters>({
    page: 1,
    limit: 20,
  });
  const [selectedUser, setSelectedUser] = useState<ProviderUser | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<ProviderUser | null>(null);
  const [detachError, setDetachError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    providerId: '',
    confirmPassword: '',
    sendAccessEmail: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['provider-users', filters],
    queryFn: () => apiService.getProviderUsers(filters),
    select: (response) => response.data,
  });

  const { data: providersData } = useQuery({
    queryKey: ['providers'],
    queryFn: () => apiService.getProviders({ limit: 100 }),
    select: (response) => response.providers || [],
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => apiService.createProviderUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-users'] });
      setDialogOpen(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiService.updateProviderUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-users'] });
      setDialogOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ userId, providerId }: { userId: number; providerId: number }) =>
      apiService.detachProviderUser(userId, providerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-users'] });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setDetachError(null);
    },
    onError: (error: any) => {
      const status = error.response?.status;
      const apiError = error.response?.data?.error;
      if (status === 404 && !apiError) {
        setDetachError(
          'Le détachement n’est pas encore disponible sur cette API. Déployez la dernière version du backend Symfony sur api26.sherlook.fr.'
        );
        return;
      }
      setDetachError(apiError || 'Impossible de détacher cet utilisateur.');
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'activate' | 'deactivate' }) => 
      action === 'activate' ? apiService.activateProviderUser(id) : apiService.deactivateProviderUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-users'] });
    },
  });

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      providerId: '',
      confirmPassword: '',
      sendAccessEmail: true,
    });
    setSelectedUser(null);
    setFormError(null);
  };

  const handleAddUser = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleEditUser = (user: ProviderUser) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      providerId: user.provider?.id.toString() || '',
      confirmPassword: '',
      sendAccessEmail: true,
    });
    setDialogOpen(true);
  };

  const handleDeleteUser = (user: ProviderUser) => {
    setDetachError(null);
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (userToDelete?.provider?.id) {
      deleteMutation.mutate({ userId: userToDelete.id, providerId: userToDelete.provider.id });
    }
  };

  const providerUserCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const user of data?.users ?? []) {
      if (user.provider?.id && !user.is_temporary_access) {
        counts.set(user.provider.id, (counts.get(user.provider.id) ?? 0) + 1);
      }
    }
    return counts;
  }, [data?.users]);

  const canDetachUser = (user: ProviderUser) => {
    const providerId = user.provider?.id;
    if (!providerId) return false;
    return (providerUserCounts.get(providerId) ?? 0) > 1;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData: any = {
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      providerId: parseInt(formData.providerId),
      sendAccessEmail: formData.sendAccessEmail,
    };

    setFormError(null);
    if (formData.password) {
      if (formData.password.length < 8 || !/[A-Z]/.test(formData.password) || !/\d/.test(formData.password)) {
        setFormError('Le mot de passe doit contenir au moins 8 caracteres, une majuscule et un chiffre.');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setFormError('Les mots de passe ne correspondent pas.');
        return;
      }
    } else if (!selectedUser && !formData.sendAccessEmail) {
      setFormError("Definissez un mot de passe si l'email d'acces n'est pas envoye.");
      return;
    }

    if (selectedUser) {
      if (formData.password) {
        submitData.password = formData.password;
      }
      updateMutation.mutate({ id: selectedUser.id, data: submitData });
    } else {
      submitData.password = formData.password;
      createMutation.mutate(submitData);
    }
  };

  const handleFilterChange = (key: keyof ProviderUserFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleToggleStatus = (user: ProviderUser) => {
    const action = user.isActive ? 'deactivate' : 'activate';
    toggleStatusMutation.mutate({ id: user.id, action });
  };

  const getRoleLabel = (roles: string[]) => {
    // Vérifier que roles existe et est un tableau
    if (!roles || !Array.isArray(roles)) {
      return 'Utilisateur';
    }
    
    const roleLabels: string[] = [];
    
    // Trier les rôles par ordre de priorité pour un affichage cohérent
    const roleOrder = ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_LOCATION_ADMIN', 'ROLE_PROVIDER', 'ROLE_USER'];
    
    roleOrder.forEach(role => {
      if (roles.includes(role)) {
        switch (role) {
          case 'ROLE_SUPER_ADMIN':
            roleLabels.push('Super Admin');
            break;
          case 'ROLE_ADMIN':
            roleLabels.push('Admin');
            break;
          case 'ROLE_LOCATION_ADMIN':
            roleLabels.push('Admin Partenaire');
            break;
          case 'ROLE_PROVIDER':
            roleLabels.push('Partenaire');
            break;
          case 'ROLE_USER':
            roleLabels.push('Utilisateur');
            break;
        }
      }
    });
    
    return roleLabels.length > 0 ? roleLabels.join(' - ') : 'Utilisateur';
  };

  const getRoleColor = (roles: string[]) => {
    // Vérifier que roles existe et est un tableau
    if (!roles || !Array.isArray(roles)) {
      return 'default';
    }
    
    // Retourner la couleur du rôle le plus élevé
    if (roles.includes('ROLE_SUPER_ADMIN')) return 'error';
    if (roles.includes('ROLE_ADMIN')) return 'warning';
    if (roles.includes('ROLE_LOCATION_ADMIN')) return 'info';
    if (roles.includes('ROLE_PROVIDER')) return 'secondary';
    return 'default';
  };

  if (error) {
    return (
      <Alert severity="error">
        Erreur lors du chargement des utilisateurs partenaires
      </Alert>
    );
  }

  return (
    <Box>
      <PageHeader
        title={<Typography variant="h4">Utilisateurs Partenaires</Typography>}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddUser}>
            Ajouter un utilisateur
          </Button>
        }
      />

      {/* Filtres */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Rechercher"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Partenaire</InputLabel>
                <Select
                  value={filters.providerId || ''}
                  onChange={(e) => handleFilterChange('providerId', e.target.value)}
                  label="Partenaire"
                >
                  <MenuItem value="">Tous</MenuItem>
                  {providersData?.map((provider: any) => (
                    <MenuItem key={provider.id} value={provider.id}>
                      {provider.name} ({provider.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Par page</InputLabel>
                <Select
                  value={filters.limit || 20}
                  onChange={(e) => handleFilterChange('limit', e.target.value)}
                  label="Par page"
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {/* Deuxième ligne de filtres */}
          <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Rôle</InputLabel>
                <Select
                  value={filters.role || ''}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  label="Rôle"
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="ROLE_USER">Utilisateur</MenuItem>
                  <MenuItem value="ROLE_PROVIDER">Partenaire</MenuItem>
                  <MenuItem value="ROLE_LOCATION_ADMIN">Admin Partenaire</MenuItem>
                  <MenuItem value="ROLE_ADMIN">Admin</MenuItem>
                  <MenuItem value="ROLE_SUPER_ADMIN">Super Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tableau des utilisateurs */}
      <Card>
        <CardContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Utilisateur</TableCell>
                      <TableCell>Partenaire</TableCell>
                      <TableCell>Rôles</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell>Créé le</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.users?.map((user: ProviderUser) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {user.fullName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {user.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {user.provider ? (
                            <Box display="flex" alignItems="center">
                              <BusinessIcon sx={{ mr: 1, color: 'action.active' }} />
                              <Box>
                                <Typography variant="body2">
                                  {user.provider.name}
                                </Typography>
                                <Chip
                                  label={user.provider.type}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Aucun
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getRoleLabel(user.roles)}
                            size="small"
                            color={getRoleColor(user.roles) as any}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={user.isActive ? 'Actif' : 'Inactif'}
                              size="small"
                              color={user.isActive ? 'success' : 'error'}
                            />
                            {user.isVerified && (
                              <CheckCircleIcon color="success" fontSize="small" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {new Date(user.createdAt).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={user.isActive}
                                  onChange={() => handleToggleStatus(user)}
                                  disabled={toggleStatusMutation.isPending}
                                />
                              }
                              label=""
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleEditUser(user)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteUser(user)}
                              color="error"
                              disabled={!canDetachUser(user) || deleteMutation.isPending}
                              title={
                                canDetachUser(user)
                                  ? 'Détacher du partenaire'
                                  : 'Impossible de détacher le seul utilisateur du partenaire'
                              }
                            >
                              <LinkOffIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {data?.pagination && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.pagination.pages}
                    page={data.pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog pour ajouter/modifier un utilisateur */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedUser ? 'Modifier l\'utilisateur partenaire' : 'Ajouter un utilisateur partenaire'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Mot de passe"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required={!selectedUser && !formData.sendAccessEmail}
                  margin="normal"
                  helperText={selectedUser ? 'Laisser vide pour ne pas changer' : 'Minimum 8 caracteres, 1 majuscule, 1 chiffre'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Confirmer le mot de passe"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  required={!!formData.password}
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword((v) => !v)} edge="end">
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Prénom"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nom"
                  value={formData.lastName}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.sendAccessEmail}
                      onChange={(e) => setFormData(prev => ({ ...prev, sendAccessEmail: e.target.checked }))}
                    />
                  }
                  label="Envoyer un email d'acces et de choix du mot de passe"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Partenaire</InputLabel>
                  <Select
                    value={formData.providerId}
                    onChange={(e) => setFormData(prev => ({ ...prev, providerId: e.target.value }))}
                    label="Partenaire"
                    required
                  >
                    {providersData?.map((provider: any) => (
                      <MenuItem key={provider.id} value={provider.id}>
                        {provider.name} ({provider.type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) ? (
                <CircularProgress size={20} />
              ) : (
                selectedUser ? 'Modifier' : 'Ajouter'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de confirmation de détachement */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleteMutation.isPending) {
            setDeleteDialogOpen(false);
            setUserToDelete(null);
            setDetachError(null);
          }
        }}
      >
        <DialogTitle>Détacher du partenaire</DialogTitle>
        <DialogContent>
          {detachError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detachError}
            </Alert>
          )}
          <Typography>
            Détacher l&apos;utilisateur &quot;{userToDelete?.fullName}&quot; du partenaire
            {userToDelete?.provider?.name ? ` "${userToDelete.provider.name}"` : ''} ?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Le compte Sherlook sera conservé. L&apos;utilisateur pourra être réassocié à un partenaire plus tard.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setUserToDelete(null);
              setDetachError(null);
            }}
            disabled={deleteMutation.isPending}
          >
            Annuler
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending || !userToDelete?.provider?.id}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Détacher'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProviderUsersPage;
